import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useProjetoRevisaoFilters } from "../hooks/Revisao_listasolicitar/filter";
import { useProjetoRevisaoPagination } from "../hooks/Revisao_listasolicitar/pagination";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { ProjetoRevisaoLista } from "../../domain/models/revisao_projeto_lista";

type LoadProjetoRevisaoState = {
  idsProjeto?: number[];
  idsCentroCusto?: number[];
  idsSolicitante?: number[];
  status?: number[];
  dataDe?: Date | null;
  dataAte?: Date | null;

  page?: number | null;
  rowsPerPage?: number | null;
};

export const useRevisaoListaSolicitarViewModel = () => {

  const {
    projetos: {
      getAllProjetoRevisaoUseCase
    },

    commons: {
      getCentroCustoDropDownUseCase,
      getFuncionariosDropDownUseCase,
      getProjetosDropDownUseCase,
    },

  } = useDependencyInjection();

  const { pushErrorNotification } = usePageLoader();
  const filters = useProjetoRevisaoFilters();
  const pagination = useProjetoRevisaoPagination();
  const debouncedLoad = useCreateDebounceHandler(750);

  const [listaRevisao, setListaRevisao] = useState<ProjetoRevisaoLista[]>();
  const [loading, setLoading] = useState(false);

  const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>([]);
  const [ddlProjeto, setDdlProjeto] = useState<DropDown[]>([]);
  const [ddlFuncionario, setDdlFuncionario] = useState<DropDown[]>([]);

  const [ddlStatus, setDdlStatus] = useState<DropDown[]>([]);

  const backOrNavigate = useBackOrNavigate();

  const statusOptions = [
    { title: "Todas", id: 3 },
    { title: "Aberta", id: 0 },
    { title: "Em análise", id: 1 },
    { title: "Encerrada", id: 2 },
  ];

  //#region  CARREGAMENTO
  const loadAllProjetoRevisaoLista = useCallback(
    async (funcState?: LoadProjetoRevisaoState) => {
      try {
        const filteredIdsCentroCusto = evaluateParamValue(funcState?.idsCentroCusto, filters.idsCentroCusto);
        const filteredIdsProjeto = evaluateParamValue(funcState?.idsProjeto, filters.idsProjeto);
        const filteredIdsSolicitante = evaluateParamValue(funcState?.idsSolicitante, filters.idsSolicitante);
        const filteredStatus = evaluateParamValue(funcState?.status, filters.status);
        const filteredDataDe = evaluateParamValue(funcState?.dataDe, filters.dataDe);
        const filteredDataAte = evaluateParamValue(funcState?.dataAte, filters.dataAte);

        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllProjetoRevisaoUseCase.execute(
          filteredIdsProjeto ?? [],
          filteredIdsCentroCusto ?? [],
          filteredIdsSolicitante ?? [],
          filteredStatus ?? [],
          filteredDataDe ?? undefined,
          filteredDataAte ?? undefined,
          filteredPage,
          filteredRowsPerPage,

        );
        setListaRevisao(getAllProjetoRevisaoUseCase.items);

        pagination.setRowsPerPage(getAllProjetoRevisaoUseCase.pageSize);
        pagination.setTotalItems(getAllProjetoRevisaoUseCase.totalItems);
      } catch (e: unknown) {
        setListaRevisao([]);
        pushErrorNotification("Error", processError(e, "Falha ao buscar lista de projetos!"));
      }
    },
    [
      filters,
      pagination,
      getAllProjetoRevisaoUseCase,
      pushErrorNotification
    ],
  );

  const loadCentroCustoDropDown = useCallback(async () => {
    try {
      const response = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      setDdlCentroCusto(response);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Centro de Custo!"));
    }
  }, [getCentroCustoDropDownUseCase, pushErrorNotification]);


  const loadFuncionarioDropDown = useCallback(async () => {
    try {
      const response = await getFuncionariosDropDownUseCase.getFuncionariosDropDown();
      setDdlFuncionario(response.map((f) => ({ id: f.idFuncionario, title: f.nome })));
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Funcionários!"));
    }
  }, [getFuncionariosDropDownUseCase, pushErrorNotification]);


  const loadProjetosDropDown = useCallback(async () => {
    try {
      const response = await getProjetosDropDownUseCase.execute();
      setDdlProjeto(response);

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar Projetos!"));
    }
  }, [getProjetosDropDownUseCase, pushErrorNotification]);

  //#endregion


  //#region PAGE START
  usePageStartBootstrap(async () => {
    try {
      await loadAllProjetoRevisaoLista();
      await loadCentroCustoDropDown();
      await loadFuncionarioDropDown();
      await loadProjetosDropDown();
      setDdlStatus(statusOptions);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados!"));
    }
  });

  //#endregion


  //#region  PAGINAÇÃO
  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        await loadAllProjetoRevisaoLista({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao paginar!"));
      } finally {
        setLoading(false);
      }
    },
    [loadAllProjetoRevisaoLista, pushErrorNotification],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        await loadAllProjetoRevisaoLista({ page: 1, rowsPerPage: rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao paginar!"));
      } finally {
        setLoading(false);
      }
    },
    [loadAllProjetoRevisaoLista, pushErrorNotification],
  );

  //#endregion



  //#region  PESQUISA

  const handleProjetoChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setIdsProjeto(newValue);
      return async () => await loadAllProjetoRevisaoLista({ idsProjeto: newValue });
    },
    [filters, loadAllProjetoRevisaoLista],
  );

  const handleCentroCustoChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setIdsCentroCusto(newValue);
      return async () => await loadAllProjetoRevisaoLista({ idsCentroCusto: newValue });
    },
    [filters, loadAllProjetoRevisaoLista],
  );

  const handleSolicitanteChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setIdsSolicitante(newValue);
      return async () => await loadAllProjetoRevisaoLista({ idsSolicitante: newValue });
    },
    [filters, loadAllProjetoRevisaoLista],
  );

  const handleStatusChange = useDebouncedCallback(
    debouncedLoad,
    (newValue: number[]) => {
      filters.setStatus(newValue);
      return async () => await loadAllProjetoRevisaoLista({ status: newValue });
    },
    [filters, loadAllProjetoRevisaoLista],
  );

  const handleDataChange = useDebouncedCallback(
    debouncedLoad,
    (de: Date | null, ate: Date | null) => {
      filters.setPeriodo(de, ate);
      return async () => await loadAllProjetoRevisaoLista({ dataDe: de, dataAte: ate });
    },
    [filters, loadAllProjetoRevisaoLista],
  );

  const handleClear = useCallback(async () => {
    filters.clear();
    await loadAllProjetoRevisaoLista({
      idsProjeto: [],
      idsCentroCusto: [],
      idsSolicitante: [],
      status: undefined,
      dataDe: null,
      dataAte: null,

      page: 1,
      rowsPerPage: pagination.rowsPerPage,
    });
  }, [filters, pagination.rowsPerPage, loadAllProjetoRevisaoLista]);

  //#endregion

  //#region CRIAÇÃO E EDIÇÃO

  const handleEdit = useCallback(
    (id: number | null, openState: boolean) => {
      if (!openState) window.open(`/Revisao_cadastrosolicitar?id=${id}&visualizar=false`, "_blank", "noopener,noreferrer");
      else backOrNavigate(`/Revisao_cadastrosolicitar?id=${id}&visualizar=false`);
    },
    [backOrNavigate],
  );

  const handleVisualizar = useCallback(
    (id: number | null, openState: boolean) => {
      if (!openState) window.open(`/Revisao_cadastrosolicitar?id=${id}&visualizar=true`, "_blank", "noopener,noreferrer");
      else backOrNavigate(`/Revisao_cadastrosolicitar?id=${id}&visualizar=true`);
    },
    [backOrNavigate],
  );

  const handleCreate = useCallback(() => backOrNavigate(`/Revisao_cadastrosolicitar?visualizar=false`), [backOrNavigate]);

  //#endregion

  return {
    listaRevisao,
    filters,
    pagination,
    loading,
    ddlCentroCusto: ddlCentroCusto ?? [],
    ddlProjeto: ddlProjeto ?? [],
    ddlFuncionario: ddlFuncionario ?? [],
    ddlStatus: ddlStatus ?? [],

    loadAllProjetoRevisaoLista,
    handlePageChange,
    handleRowsPerPageChange,
    handleClear,
    handleProjetoChange,
    handleCentroCustoChange,
    handleSolicitanteChange,
    handleStatusChange,
    handleDataChange,
    handleVisualizar,
    handleCreate,
    handleEdit,
  };
};
